import { useNavigate } from 'react-router-dom';
import { useOnboardingStore } from '@/store/onboardingStore';
import { useAthleteStore } from '@/store';
import {
    Brain,
    Timer,
    Dumbbell,
    Flame,
    ChevronRight,
    Zap,
    Play
} from 'lucide-react';

// New UI System
import { PageShell } from '@/components/ui/PageShell';
import { EliteCard } from '@/components/ui/EliteCard';
import { SectionHeader } from '@/components/ui/SectionHeader';
import { PrimaryButton, SecondaryButton } from '@/components/ui/Buttons';

export function TrainingHubPage() {
    const navigate = useNavigate();
    const { profile } = useOnboardingStore();
    const athleteName = useAthleteStore(s => s.athletes.find(a => a.id === s.activeId)?.name) || 'Athlete';

    // ─── Personalization Engine ──────────────────────────────────────
    const getDailyFocus = () => {
        const style = profile.matIdentity || 'Unknown';
        const attack = profile.topAttack || 'Neutral';
        const level = profile.level || 'youth';

        if (level === 'youth') return {
            title: 'Stance in Motion',
            subtitle: 'Master the fundamentals of movement',
            icon: Zap,
            color: 'text-blue-400',
            bg: 'bg-blue-500/20'
        };

        if (style === 'technician') return {
            title: 'Top Control Pressure',
            subtitle: 'Maintain dominance and breakdown',
            icon: Dumbbell,
            color: 'text-orange-400',
            bg: 'bg-orange-500/20'
        };

        if (style === 'relentless_shooter' || attack === 'single_leg') return {
            title: 'Explosive Finishes',
            subtitle: 'Chain wrestling from the shot',
            icon: Flame,
            color: 'text-red-400',
            bg: 'bg-red-500/20'
        };

        return {
            title: 'Match Strategy',
            subtitle: 'Situational awareness and scoring',
            icon: Brain,
            color: 'text-gold-400',
            bg: 'bg-gold-500/20'
        };
    };

    const dailyFocus = getDailyFocus();

    return (
        <PageShell
            title="Training Hub"
            subtitle={`Ready, ${athleteName.split(' ')[0]}?`}
            right={
                <div className="bg-bg-elevated border border-white/10 rounded-full px-3 py-1 flex items-center gap-2">
                    <Flame className="w-3 h-3 text-orange-500 fill-orange-500 animate-pulse" />
                    <span className="text-[10px] font-bold text-white">3 Day Streak</span>
                </div>
            }
        >
            <div className="space-y-8 animate-fade-in">

                {/* Daily Focus Card (Hero) */}
                <section>
                    <SectionHeader title="Daily Focus" meta="AI RECOMMENDED" className="mb-3" />
                    <EliteCard className="relative overflow-hidden group p-6">
                        <div className="absolute top-0 right-0 p-4 opacity-5 group-hover:opacity-10 group-hover:scale-110 transition-all duration-500">
                            <dailyFocus.icon size={120} />
                        </div>

                        <div className="relative z-10 space-y-4">
                            <div>
                                <h2 className="text-[28px] font-black uppercase text-white tracking-tight leading-none mb-1 shadow-sm">
                                    {dailyFocus.title}
                                </h2>
                                <p className="text-text-secondary text-sm font-medium">{dailyFocus.subtitle}</p>
                            </div>

                            <PrimaryButton onClick={() => navigate('/train/session?focus=' + dailyFocus.title)} className="w-fit mt-4">
                                <Play className="w-4 h-4 mr-2 fill-current" /> Start Session
                            </PrimaryButton>
                        </div>
                    </EliteCard>
                </section>

                {/* Training Modes */}
                <section>
                    <SectionHeader title="Training Modes" meta="REAL-TIME TOOLS" className="mb-3" />

                    <div className="grid gap-3">
                        {/* Mode 1: Stance & Motion */}
                        <button
                            onClick={() => navigate('/train/stance-motion')}
                            className="flex items-center gap-4 p-4 rounded-2xl bg-bg-surface border border-white/5 hover:bg-white/5 hover:border-blue-500/30 transition-all group text-left shadow-[0_4px_20px_rgba(0,0,0,0.4)]"
                        >
                            <div className="w-12 h-12 rounded-xl bg-blue-500/10 border border-blue-500/20 flex items-center justify-center group-hover:scale-110 transition-transform">
                                <Zap className="w-5 h-5 text-blue-400" />
                            </div>
                            <div className="flex-1">
                                <div className="font-bold text-white text-sm">Stance & Motion</div>
                                <div className="text-[10px] font-medium text-text-muted mt-0.5 uppercase tracking-wider">Audio-guided movement drills</div>
                            </div>
                            <ChevronRight className="w-5 h-5 text-text-muted group-hover:text-white transition-colors" />
                        </button>

                        {/* Mode 2: Whistle Starts */}
                        <button
                            onClick={() => navigate('/train/session?mode=whistle')}
                            className="flex items-center gap-4 p-4 rounded-2xl bg-bg-surface border border-white/5 hover:bg-white/5 hover:border-orange-500/30 transition-all group text-left shadow-[0_4px_20px_rgba(0,0,0,0.4)]"
                        >
                            <div className="w-12 h-12 rounded-xl bg-orange-500/10 border border-orange-500/20 flex items-center justify-center group-hover:scale-110 transition-transform">
                                <Timer className="w-5 h-5 text-orange-400" />
                            </div>
                            <div className="flex-1">
                                <div className="font-bold text-white text-sm">Whistle Starts</div>
                                <div className="text-[10px] font-medium text-text-muted mt-0.5 uppercase tracking-wider">10s Work / 10s Rest Repeats</div>
                            </div>
                            <ChevronRight className="w-5 h-5 text-text-muted group-hover:text-white transition-colors" />
                        </button>

                        {/* Mode 3: Custom Match Timer */}
                        <button
                            onClick={() => navigate('/train/session')}
                            className="flex items-center gap-4 p-4 rounded-2xl bg-bg-surface border border-white/5 hover:bg-white/5 hover:border-green-500/30 transition-all group text-left shadow-[0_4px_20px_rgba(0,0,0,0.4)]"
                        >
                            <div className="w-12 h-12 rounded-xl bg-green-500/10 border border-green-500/20 flex items-center justify-center group-hover:scale-110 transition-transform">
                                <Dumbbell className="w-5 h-5 text-green-400" />
                            </div>
                            <div className="flex-1">
                                <div className="font-bold text-white text-sm">Match Timer</div>
                                <div className="text-[10px] font-medium text-text-muted mt-0.5 uppercase tracking-wider">Custom rounds & dual meet mode</div>
                            </div>
                            <ChevronRight className="w-5 h-5 text-text-muted group-hover:text-white transition-colors" />
                        </button>

                        {/* Link to Pricing / Team */}
                        <SecondaryButton onClick={() => navigate('/pricing')} className="mt-2 text-gold-400 border-gold-500/20 hover:border-gold-500/50 hover:bg-gold-500/5">
                            View Team Plans & Pricing
                        </SecondaryButton>
                    </div>
                </section>

                {/* AI Coach Teaser */}
                <section>
                    <div className="p-4 rounded-2xl bg-gradient-to-r from-purple-900/40 to-blue-900/40 border border-purple-500/20 flex items-center gap-4 relative overflow-hidden shadow-[0_10px_30px_rgba(0,0,0,0.5)]">
                        <div className="w-10 h-10 rounded-full bg-purple-500/20 flex items-center justify-center shrink-0 border border-purple-500/50">
                            <Brain className="w-5 h-5 text-purple-300" />
                        </div>
                        <div className="flex-1 relative z-10">
                            <h3 className="font-bold text-white text-sm">Coach Gable AI</h3>
                            <p className="text-[11px] font-medium text-purple-200/80 leading-tight mt-1 opacity-80 italic">
                                "Ask me about setting up your {profile.topAttack ? profile.topAttack.replace('_', ' ') : 'attacks'} for your next match."
                            </p>
                        </div>
                    </div>
                </section>
            </div>
        </PageShell>
    );
}
